import { Aes256Gcm, CipherSuite, DhkemP256HkdfSha256, HkdfSha256 } from '@hpke/core';

/**
 * HPKE (RFC 9180) base-mode sender, used to seal credentials before they leave
 * the browser — see PASSWORD_ENCRYPTION-v2.md.
 *
 * The ciphersuite is fixed and must match the auth service exactly. There is no
 * negotiation: any divergence derives a different AES key, and the service
 * answers AUTH_004 with no diagnostic as to why.
 *
 * Instances rather than KemId/KdfId/AeadId numbers: @hpke/core rejects the
 * numeric form that the older hpke-js meta-package accepts.
 */
const suite = new CipherSuite({
  kem: new DhkemP256HkdfSha256(),   // 0x0010
  kdf: new HkdfSha256(),            // 0x0001
  aead: new Aes256Gcm()             // 0x0002
});

/** Feeds the key derivation — must match the service byte for byte. */
const HPKE_INFO = new TextEncoder().encode('p4c-b2b-password-v1');

/**
 * Seals one field against the service's public key and returns the wire form:
 * base64 of the ephemeral public key (65 bytes) followed by the ciphertext and
 * its 16-byte tag.
 *
 * seal() hands the two halves back separately; they are joined as raw bytes and
 * encoded once. Encoding them separately and concatenating the strings produces
 * a payload the backend cannot read.
 *
 * The value is sealed exactly as given — no trimming or normalising, since the
 * service compares against a hash of the original.
 */
export async function sealToBase64(recipientPublicKeyBase64: string, plaintext: string): Promise<string> {
  const recipientPublicKey = await suite.kem.deserializePublicKey(base64ToBytes(recipientPublicKeyBase64));
  const { enc, ct } = await suite.seal(
    { recipientPublicKey, info: HPKE_INFO },
    new TextEncoder().encode(plaintext)
  );

  const sealed = new Uint8Array(enc.byteLength + ct.byteLength);
  sealed.set(new Uint8Array(enc));
  sealed.set(new Uint8Array(ct), enc.byteLength);
  return bytesToBase64(sealed);
}

/** Standard base64 with padding — the backend's decoder rejects the URL-safe alphabet. */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), character => character.charCodeAt(0));
}
